import { getEmailContent } from "../api/tools.ts";

/** Fetch a sent email's HTML and render it in a new browser window.
 *  Replaces WebAdmin2's MAIL/ViewMail.aspx form-POST flow — that endpoint
 *  no longer exists; the server returns the rendered HTML through the
 *  authenticated `WebGetEmailContent` JSON method instead.
 *
 *  Uses a blob URL so the popup has a real address: the user can copy it,
 *  bookmark it, refresh it, or open it in another tab. The URL is revoked
 *  shortly after the popup loads (no permanent reference into client memory).
 *
 *  Returns an error message on failure (caller decides whether to toast),
 *  null on success. */
export async function openEmailViewer(
  transactionID: string | number,
  emailLogID: string | number,
): Promise<string | null> {
  const tid = Number(transactionID);
  const lid = Number(emailLogID);
  if (!Number.isFinite(tid) || !Number.isFinite(lid)) {
    return "Invalid transaction or email ID.";
  }

  let html: string;
  try {
    const result = await getEmailContent(tid, lid);
    if (result.bError) return result.sMessage || "Failed to load email";
    html = result.oData ?? "";
  } catch (err) {
    return String(err);
  }

  const blob = new Blob([html || "<p>No content</p>"], { type: "text/html;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const popup = window.open(url, "_blank", "width=900,height=700");
  if (!popup) {
    URL.revokeObjectURL(url);
    return "Popup blocked — allow popups to view email content.";
  }
  // Give the popup time to load the resource before releasing the URL.
  // 60s is generous; the browser still has its own copy once it's loaded.
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
  return null;
}
